import { i18n } from "@/boot/i18n";
import TeamService from "@/services/team";
import ErrorHandler from "@/tools/errorHandler";
import { REQUIRED } from "../validator/methods";
import * as types from "./mutationTypes";

export default {
  initListState({ commit }) {
    commit(types.SET_TEAMS_TREE, []);
    commit(types.SET_TEAMS, []);
  },
  clearListState({ commit }) {
    commit(types.CLEAR_TEAMS_TREE);
    commit(types.CLEAR_TEAMS);
    commit(types.CLEAR_TEAM);
  },
  setTeamsTreeSuccess({ commit }, teams) {
    commit(types.SET_TEAMS_TREE, teams);
  },
  setTeamsSuccess({ commit }, teams) {
    commit(types.SET_TEAMS, teams);
  },
  setTeamSuccess({ commit }, team) {
    commit(types.SET_TEAM, team);
  },
  initNewState({ dispatch }) {
    dispatch("survey/setSurveys", [], { root: true });
    dispatch("metadata/setMetadatas", [], { root: true });
    const validator = validators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
    dispatch("validator/addValidator", validator.colorValidator, { root: true });
  },
  clearNewState({ commit, dispatch }) {
    commit(types.CLEAR_TEAM);
    dispatch("user/clearUsers", null, { root: true });
    dispatch("survey/clearSurveys", null, { root: true });
    dispatch("metadata/clearMetadatas", null, { root: true });
    dispatch("validator/clearValidator", null, { root: true });
  },
  async getAllTree({ rootState, dispatch }) {
    try {
      const response = await TeamService.getAllTree(rootState.organization.organization.uuid);
      if (response.data.data) {
        dispatch("setTeamsTreeSuccess", response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAll({ rootState, dispatch }, pagination) {
    try {
      const response = await TeamService.getAll(rootState.organization.organization.uuid, pagination);
      if (response.data.data) {
        dispatch("setTeamsSuccess", response.data.data);
      }
    } catch (error) {
      if (error.response.data && error.response.data.message.includes("root")) {
        dispatch("ui/showToastError", i18n.t("team.messages.delete_root"), { root: true });
      } else {
        dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      }
    }
  },
  async delete({ rootState, dispatch }, { teamUUID, newTeamUUID }) {
    try {
      await TeamService.delete(rootState.organization.organization.uuid, teamUUID, newTeamUUID);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOne({ rootState, dispatch }, teamUUID) {
    try {
      const response = await TeamService.getOne(rootState.organization.organization.uuid, teamUUID);
      if (response.data.data) {
        dispatch("setTeamSuccess", response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAccess({ rootState, dispatch }, parentUUID) {
    try {
      const response = await TeamService.getAccess(rootState.organization.organization.uuid, parentUUID);
      if (response.data.data) {
        dispatch("setTeamSuccess", response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async reparent({ rootState, dispatch }, { teamUUID, parentUUID }) {
    try {
      await TeamService.reparent(rootState.organization.organization.uuid, teamUUID, parentUUID);
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async create({ rootState, dispatch }, team) {
    try {
      await TeamService.create(rootState.organization.organization.uuid, team);
      dispatch("ui/showToastSuccess", i18n.t("team.messages.create_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async update({ rootState, dispatch }, team) {
    try {
      await TeamService.update(rootState.organization.organization.uuid, team);
      dispatch("organization/getOne", null, { root: true });
      dispatch("ui/showToastSuccess", i18n.t("team.messages.update_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async save({ state, dispatch }) {
    const valid = await dispatch("validate");
    if (!valid) {
      return false;
    }
    const team = getPermissionsToSave(state.team);
    team.name = team.name.trim();

    if (state.team.uuid) {
      return dispatch("update", team);
    } else {
      return dispatch("create", team);
    }
  },
  async addUser({ rootState, dispatch }, { teamUUID, userUUID }) {
    try {
      await TeamService.addUser(rootState.organization.organization.uuid, teamUUID, userUUID);
      dispatch("ui/showToastSuccess", i18n.t("team.messages.add_member_success"), { root: true });
    } catch (error) {
      if (error.response.data.message && error.response.data.message.includes("already")) {
        dispatch("ui/showToastError", i18n.t("team.messages.already_in_team"), { root: true });
        return;
      }
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async removeUser({ rootState, dispatch }, { teamUUID, userUUID }) {
    try {
      await TeamService.removeUser(rootState.organization.organization.uuid, teamUUID, userUUID);
      dispatch("ui/showToastSuccess", i18n.t("team.messages.remove_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  userHasPermission({ rootState }, { resourceName, permissionName }) {
    if (!rootState.organization.organization) {
      return;
    }
    const permissions = rootState.organization.organization.permissions;
    const resourcePermissions = permissions.find((resource) => resourceName === resource.name);
    const resourcePermission = resourcePermissions.children.find((permission) => permission.name === permissionName);

    return resourcePermission.has_permission;
  },
  clearTeams({ commit }) {
    commit(types.CLEAR_TEAMS);
  },
  clearTeamsTree({ commit }) {
    commit(types.CLEAR_TEAMS_TREE);
  },
  setParent({ commit }, parentUUID) {
    commit(types.SET_PARENT, parentUUID);
  },
  setTeam({ commit }, team) {
    commit(types.SET_TEAM, team);
  },
  setName({ commit, dispatch }, name) {
    commit(types.SET_NAME, name);
    dispatch("validator/checkValidation", { field: "name", value: name }, { root: true });
  },
  setColor({ commit, dispatch }, color) {
    commit(types.SET_COLOR, color);
    dispatch("validator/checkValidation", { field: "color", value: color }, { root: true });
  },
  addMember({ commit }, user) {
    commit(types.ADD_MEMBER, user);
  },
  removeMember({ commit }, userUUID) {
    commit(types.REMOVE_MEMBER, userUUID);
  },
  validate({ dispatch, state, rootState }) {
    dispatch("validator/validate", { field: "name", value: state.team.name }, { root: true });
    dispatch("validator/validate", { field: "color", value: state.team.color }, { root: true });
    return rootState.validator.errors?.length === 0;
  },
  setPermission({ commit }, { resourceUUID, permissionUUID, permission }) {
    commit(types.SET_PERMISSION, {
      resourceUUID,
      permissionUUID,
      permission
    });
  },
  checkParentPermissions({ state, commit }) {
    /**
     * Check if parent has permission and disable resource if does not
     */
    if (!state.team.parent_restrictions) {
      return;
    }
    state.team.permissions.forEach((resource) => {
      resource.children.forEach((permission) => {
        if (state.team.parent_restrictions.includes(permission.uuid)) {
          commit(types.SET_PERMISSION_DISABLED, {
            resourceUUID: resource.uuid,
            permissionUUID: permission.uuid,
            disabled: true
          });
        }
      });
    });
  },
  checkDefaultReadValue({ state, commit }) {
    /**
     * Allteams must have Dashboard Read permission
     */
    const resources = state.team.permissions;
    const dashPermissions = resources.find((permission) => permission.name === "Dashboards");
    const readPermission = dashPermissions.children.find((permission) => permission.name === "Read");
    commit(types.SET_PERMISSION, {
      resourceUUID: dashPermissions.uuid,
      permissionUUID: readPermission.uuid,
      permission: true
    });
    commit(types.SET_PERMISSION_DISABLED, {
      resourceUUID: dashPermissions.uuid,
      permissionUUID: readPermission.uuid,
      disabled: true
    });
    /**
     * Allteams must have Organization Read permission
     */
    const organizationPermissions = resources.find((permission) => permission.name === "Organizations");
    const orgReadPermission = organizationPermissions.children.find((permission) => permission.name === "Read");
    commit(types.SET_PERMISSION, {
      resourceUUID: organizationPermissions.uuid,
      permissionUUID: orgReadPermission.uuid,
      permission: true
    });
    commit(types.SET_PERMISSION_DISABLED, {
      resourceUUID: organizationPermissions.uuid,
      permissionUUID: orgReadPermission.uuid,
      disabled: true
    });
  },
  populateMetadataPermission({ rootState, state, commit }) {
    /**
     * Load metadata specifics permissions
     * Check if parent has permission too
     */
    const metadataResource = state.team.permissions.find((resource) => resource.name === "Attributes");

    if (rootState.metadata.metadatas) {
      rootState.metadata.metadatas.map((metadata) => {
        let parentRestriction = false;
        if (state.team.parent_tags_restrictions) {
          parentRestriction = state.team.parent_tags_restrictions.includes(metadata.uuid);
        }
        const permission = state.team.tags ? state.team.tags[metadata.uuid] : false;
        if (!metadataResource.children.find((permission) => permission.uuid === metadata.uuid)) {
          const permissionData = {
            uuid: metadata.uuid,
            name: metadata.name,
            permission: permission,
            disable: parentRestriction,
            default: false
          };
          commit(types.ADD_PERMISSION, { resourceUUID: metadataResource.uuid, permission: permissionData });
        }
      });
    }
  },
  populateSurveyPermission({ rootState, state, commit }) {
    /**
     * Load survey specifics permissions
     * Check if parent has permission too
     */
    const surveyResource = state.team.permissions.find((resource) => resource.name === "Surveys");

    if (rootState.survey.surveys) {
      rootState.survey.surveys.map((survey) => {
        let parentRestriction = false;
        if (state.team.parent_uuid) {
          if (state.team.parent_surveys) {
            parentRestriction = !state.team.parent_surveys.includes(survey.uuid);
          } else {
            parentRestriction = true;
          }
        }
        const permission = state.team.surveys ? state.team.surveys.includes(survey.uuid) : false;
        if (!surveyResource.children.find((permission) => permission.uuid === survey.uuid)) {
          const permissionData = {
            uuid: survey.uuid,
            name: survey.name,
            permission: permission,
            disable: parentRestriction,
            default: false
          };
          commit(types.ADD_PERMISSION, { resourceUUID: surveyResource.uuid, permission: permissionData });
        }
      });
    }
  }
};

const validators = () => {
  return {
    nameValidator: {
      field: "name",
      validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }]
    },
    colorValidator: {
      field: "color",
      validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }]
    }
  };
};

function getPermissionsToSave(teamState) {
  const team = { ...teamState };

  if (!team.tags) {
    team.tags = {};
  }

  // Get the metadata values and put in tags object
  const metadataResourceIndex = team.permissions.findIndex((permission) => permission.name === "Attributes");
  team.permissions[metadataResourceIndex].children.forEach((permission) => {
    if (!permission.default) {
      // Isn't part of default permissions (READ, CREATE, etc)
      team.tags[permission.uuid] = permission.permission;
    }
  });
  team.permissions[metadataResourceIndex].children = team.permissions[metadataResourceIndex].children.filter(
    (permission) => permission.default
  );

  team.surveys = [];
  // Get the survey values and put in surveys object
  const surveyResourceIndex = team.permissions.findIndex((permission) => permission.name === "Surveys");
  team.permissions[surveyResourceIndex].children.forEach((permission) => {
    if (!permission.default && permission.permission) {
      // Isn't part of default permissions (READ, CREATE, etc)
      team.surveys.push(permission.uuid);
    }
  });
  team.permissions[surveyResourceIndex].children = team.permissions[surveyResourceIndex].children.filter(
    (permission) => permission.default
  );

  return team;
}
